import { Grid, Typography, Paper, Box } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsClock, BsClockHistory } from "react-icons/bs";
import { LuActivity, LuLeaf, LuTrendingDown, LuZap } from "react-icons/lu";
import TextBox from "../components/input/TextBox";
import { LCA_stages_A1_A5, LCA_stages_B1_B5, LCA_stages_C1_C4 } from "../components/LCA_Stages";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { TableHeaderContainer } from "../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../components/container/TableTitleContainer";
import { ReusabilityMetric } from "../db_access/reusability_metric";
import { getAvaliableLife, getDonorCredit, getDonorUtilityValue, getReuseMaterialGWP } from "../pages/Products/Reuse_Workspace/ReusedProduct_Calculations";
import MetricCard from "../app_components/MetricCard";

const ReuseImpactSummary = ({ processes }) => {
  const { t } = useTranslation();
  const { refresh, setIsSaveVisible, isSaveClicked, setIsSaveClicked, selectedProductIds, setIsLoading, setRefresh } = useContextProvider();
  const [reusabilityInfo, setReusabilityInfo] = useState({ standard_shelf_life: undefined, actual_life_used: undefined });
  const [emissionA, setEmissionA] = useState(0);
  const [emissionB, setEmissionB] = useState(0);
  const [emissionC, setEmissionC] = useState(0);
  const [modified, setModified] = useState(false);
  const fields = [
    {
      label: "Standard Shelf Life (Years)",
      value: "standard_shelf_life",
      icon: BsClockHistory,
      type: "number",
      tooltip: t("Standard Shelf Life can also be Industry Average Life."),
    },
    {
      label: "Actual Life Used (Years)",
      value: "actual_life_used",
      icon: BsClock,
      type: "number",
      tooltip: t("Actual Life Used should be equal or less than Standard Shelf Life."),
    },
  ];
  useEffect(() => {
    if (reusabilityInfo?.actual_life_used > reusabilityInfo?.standard_shelf_life) {
      setReusabilityInfo((p) => ({ ...p, actual_life_used: reusabilityInfo.standard_shelf_life }));
    }
  }, [reusabilityInfo]);
  const selectedProductId = useMemo(() => {
    return selectedProductIds && selectedProductIds.length > 0 ? selectedProductIds[0] : null;
  }, [selectedProductIds]);

  const handleInputChange = (field, value) => {
    const numValue = value === "" ? undefined : parseFloat(value);
    console.log(`[ReuseImpact] User typed → field: "${field}", raw value: "${value}", parsed: ${numValue}, type: ${typeof numValue}`);
    setReusabilityInfo((p) => {
      const next = { ...p, [field]: numValue };
      console.log(`[ReuseImpact] reusabilityInfo after user input:`, next);
      return next;
    });
    setModified(true);
  };
  useEffect(() => {
    const getReusabilityInfo = () => {
      ReusabilityMetric.get_by_product_id(selectedProductId).then((response) => {
        if (!response.ok) {
          console.log(`[ReuseImpact] Fetch returned non-OK status (${response.status}) — keeping current state`);
          return;
        }
        response.json().then((data) => {
          console.log(`[ReuseImpact] Fetched from API (on refresh):`, data);
          if (data?.error) {
            console.log(`[ReuseImpact] API returned error in body — keeping current state`);
            return;
          }
          setReusabilityInfo(data);
        });
      });
    };
    setModified(false);
    getReusabilityInfo();
  }, [refresh, selectedProductId]);
  useEffect(() => {
    if (modified) {
      setIsSaveVisible(true);
    }
  }, [modified, setIsSaveVisible]);


  useEffect(() => {
    if (!processes || processes.length === 0) return;
    setEmissionA(processes.filter((p) => LCA_stages_A1_A5.includes(p.lca_stage)).reduce((sum, p) => sum + (p.gwp || 0), 0));
    setEmissionB(processes.filter((p) => LCA_stages_B1_B5.includes(p.lca_stage)).reduce((sum, p) => sum + (p.gwp || 0), 0));
    setEmissionC(processes.filter((p) => LCA_stages_C1_C4.includes(p.lca_stage)).reduce((sum, p) => sum + (p.gwp || 0), 0));
  }, [processes]);

  const availableLife = useMemo(() => {
    const result = getAvaliableLife(reusabilityInfo.standard_shelf_life, reusabilityInfo.actual_life_used);
    console.log(`[ReuseImpact] Computed availableLife: ${result} (standard_shelf_life=${reusabilityInfo.standard_shelf_life} [${typeof reusabilityInfo.standard_shelf_life}], actual_life_used=${reusabilityInfo.actual_life_used} [${typeof reusabilityInfo.actual_life_used}])`);
    return result;
  }, [reusabilityInfo.standard_shelf_life, reusabilityInfo.actual_life_used]);

  const donorUtilityValue = useMemo(() => {
    const result = getDonorUtilityValue(reusabilityInfo.standard_shelf_life, availableLife);
    console.log(`[ReuseImpact] Computed donorUtilityValue: ${result}`);
    return result;
  }, [reusabilityInfo.standard_shelf_life, availableLife]);

  const donorCredit = useMemo(() => {
    const result = getDonorCredit(emissionA, emissionB, emissionC, donorUtilityValue);
    console.log(`[ReuseImpact] Computed donorCredit: ${result} (emissionA=${emissionA}, emissionB=${emissionB}, emissionC=${emissionC})`);
    return result;
  }, [emissionA, emissionB, emissionC, donorUtilityValue]);

  const reuseMaterialGWP = useMemo(() => {
    const result = getReuseMaterialGWP(emissionA, donorCredit);
    console.log(`[ReuseImpact] Computed reuseMaterialGWP: ${result}`);
    console.log(`[ReuseImpact] CARD VALUES → Donor Utility: ${(donorUtilityValue * 100).toFixed(1)}%, Donor Credit: ${result !== undefined ? donorCredit : "?"}, Reuse GWP: ${result}`);
    return result;
  }, [emissionA, donorCredit]);

  useEffect(() => {
    if (!isSaveClicked) return;
    if (!modified) return;
    if (!selectedProductId) return;
    const payload = { ...reusabilityInfo, product_id: selectedProductId, reused_gwp: reuseMaterialGWP };
    console.log(`[ReuseImpact] BEFORE SAVE — reusabilityInfo being sent to API:`, payload);
    console.log(`[ReuseImpact] BEFORE SAVE — card values: donorUtilityValue=${donorUtilityValue}, donorCredit=${donorCredit}, reuseMaterialGWP=${reuseMaterialGWP}`);
    ReusabilityMetric.update_by_product_id(payload)
      .then((res) => {
        console.log(`[ReuseImpact] API save response status:`, res?.status);
        return res?.json?.();
      })
      .then((responseData) => {
        console.log(`[ReuseImpact] API save response body:`, responseData);
      })
      .catch((err) => {
        console.error(`[ReuseImpact] API save error:`, err);
      })
      .finally(() => {
        console.log(`[ReuseImpact] AFTER SAVE — reusabilityInfo in state (before re-fetch):`, reusabilityInfo);
        setIsSaveClicked(false);
        setIsLoading(false);
        setModified(false);
        setRefresh((r) => !r);
      });
  }, [isSaveClicked, modified, selectedProductId, reusabilityInfo, reuseMaterialGWP, setIsSaveClicked, setIsLoading, setRefresh]);


  const metrics = useMemo(() => [
    {
      title: t("Donor Utility Value"),
      subtitle: t("Reuse benefit ratio"),
      value: `${(donorUtilityValue * 100).toFixed(1)}`,
      unit: "%",
      icon: LuActivity,
      accent: "#16a34a",
      bg: "rgba(22, 163, 74, 0.08)",
    },
    {
      title: t("Donor Credit"),
      subtitle: t("Emission offset"),
      value: donorCredit,
      unit: "kg CO₂e",
      icon: LuZap,
      accent: "#2563eb",
      bg: "rgba(37, 99, 235, 0.08)",
    },
    {
      title: t("Reuse Material GWP"),
      subtitle: t("Net carbon footprint"),
      value: reuseMaterialGWP,
      unit: "kg CO₂e",
      icon: LuLeaf,
      accent: "#0d9488",
      bg: "rgba(13, 148, 136, 0.08)",
    },
    {
      title: t("GWP of Reuse vs Virgin"),
      subtitle: t("Compared to virgin material"),
      value: `${((reuseMaterialGWP / (emissionA || 1)) * 100).toFixed(2)}`,
      unit: "% of virgin",
      icon: LuTrendingDown,
      accent: "#7c3aed",
      bg: "rgba(124, 58, 237, 0.08)",
    },
  ], [donorUtilityValue, donorCredit, reuseMaterialGWP, emissionA, t]);

  return (
    <Paper variant="Table Container">
      <TableHeaderContainer>
        <TableTitleContainer>
          <Typography variant="Table Title">{t("Reuse Impact Summary")}</Typography>
        </TableTitleContainer>
      </TableHeaderContainer>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {fields.map((field, index) => (
            <Grid item size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <TextBox
                label={t(field.label)}
                value={reusabilityInfo[field.value]}
                onChange={(e) => handleInputChange(field.value, e.target.value)}
                Icon={field.icon}
                type={field.type}
                tooltip={field?.tooltip}
              />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2.5}>
          {metrics.map((metric, index) => (
            <Grid item size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <MetricCard
                title={metric.title}
                subtitle={metric.subtitle}
                value={metric.value}
                unit={metric.unit}
                icon={metric.icon}
                accent={metric.accent}
                bg={metric.bg}
                delay={index * 0.05}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Paper>
  );
};

export default ReuseImpactSummary;
